import {Icon, IconName} from '@benchling/nucleus-ui';
import {useId} from '@mantine/hooks';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {HTMLAttributes, ReactNode, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BetaBadge from 'ui-library/badges/BetaBadge';
import Button, {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {
  BLUE_LIGHT,
  BLUE_POWDER_LIGHT,
  GREEN_DARK,
  GREEN_LIGHTEST,
  NEAR_BLACK,
  ORANGE,
  ORANGE_LIGHTEST,
  RED,
  RED_POWDER,
  PURPLE,
  PURPLE_LIGHTEST,
  YELLOW_LIGHTEST,
  YELLOW,
} from 'ui-library/Colors';

export enum InlineBannerType {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  ATTENTION = 'ATTENTION',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  BETA = 'BETA',
}

export enum InlineBannerActionType {
  ACCORDION = 'ACCORDION',
  ACCORDION_AND_CUSTOM = 'ACCORDION_AND_CUSTOM',
  CUSTOM = 'CUSTOM',
}

interface AccordionActionProps {
  /** What sort of action button should be displayed. */
  actionType: InlineBannerActionType.ACCORDION;
  /**
   * Whether the action button should be aligned with the right-hand side of the banner instead of the end of
   * the banner text.
   */
  rightAlign?: boolean;
  /** Labels to use for the show more & show less buttons. Defaults to "Show details" and "Hide details". */
  accordionLabels?: {moreLabel: string; lessLabel: string};
  /** Whether to initialize accordion to open or closed. Defaults to closed. */
  initialShowMore?: boolean;
  /** Callback for when the accordion button is clicked. */
  onClickAccordionButton?: (newIsExpanded: boolean) => void;
}

interface ActionOnClick {
  /** What should happen when the action button is clicked. */
  actionOnClick: () => void;
  actionHref?: never;
}

interface ActionHref {
  actionOnClick?: never;
  /** Href for button link. Alternative to providing an onClick function, to support opening link in new tab. */
  actionHref: string;
}

type ActionClickProps = ActionOnClick | ActionHref;

type AccordionAndCustomActionProps = {
  /** What sort of action buttons should be displayed. */
  actionType: InlineBannerActionType.ACCORDION_AND_CUSTOM;
  /** Labels to use for the show more & show less buttons. Defaults to "Show details" and "Hide details". */
  accordionLabels?: {moreLabel: string; lessLabel: string};
  /** What the action button should read. */
  actionText: string;
  /** Whether to initialize accordion to open or closed. Defaults to closed. */
  initialShowMore?: boolean;
  /**
   * Whether the action button should be aligned with the right-hand side of the banner instead of the end of
   * the banner text.
   */
  rightAlignActionButton?: boolean;
  /**
   * Whether the accordion button should be aligned with the right-hand side of the banner instead of the end
   * of the banner text.
   */
  rightAlignAccordionButton?: boolean;
  /** What button style should be used for the custom action button. */
  buttonStyle?: ButtonStyle;
} & ActionClickProps;

type CustomActionProps = {
  /** What sort of action button should be displayed. */
  actionType: InlineBannerActionType.CUSTOM;
  /** For a custom action button, what the button should read. */
  actionText: string;
  /** Whether the action button should be aligned with the right-hand side of the banner instead of the end
   * of the banner text. */
  rightAlign?: boolean;
  /** What button style should be used for the custom action button. */
  buttonStyle?: ButtonStyle;
} & ActionClickProps;

export type InlineBannerActionProps =
  | AccordionActionProps
  | AccordionAndCustomActionProps
  | CustomActionProps;

function isAccordionActionProps(action?: {
  actionType?: InlineBannerActionType;
}): action is AccordionActionProps {
  return action?.actionType === InlineBannerActionType.ACCORDION;
}

function isCustomActionProps(action?: {actionType?: InlineBannerActionType}): action is CustomActionProps {
  return action?.actionType === InlineBannerActionType.CUSTOM;
}

/** @package */
export const inlineBannerStyles = StyleSheet.create({
  // Color-scheme related styles
  betaBadge: {
    marginRight: '8px',
  },
  betaColors: {
    backgroundColor: YELLOW_LIGHTEST,
    borderColor: YELLOW,
  },
  betaIcon: {
    color: YELLOW,
  },
  infoColors: {
    backgroundColor: BLUE_POWDER_LIGHT,
    borderColor: BLUE_LIGHT,
  },
  infoIcon: {
    color: BLUE_LIGHT,
  },
  successColors: {
    backgroundColor: GREEN_LIGHTEST,
    borderColor: GREEN_DARK,
  },
  successIcon: {
    color: GREEN_DARK,
  },
  attentionIcon: {
    color: PURPLE,
  },
  attentionColors: {
    backgroundColor: PURPLE_LIGHTEST,
    borderColor: PURPLE,
  },
  warningColors: {
    backgroundColor: ORANGE_LIGHTEST,
    borderColor: ORANGE,
  },
  warningIcon: {
    color: ORANGE,
  },
  errorColors: {
    backgroundColor: RED_POWDER,
    borderColor: RED,
  },
  errorIcon: {
    color: RED,
  },
  // Layout-related styles
  container: {
    padding: '8px 8px',
    borderLeft: '4px solid',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
  },
  containerNoBackground: {
    padding: '0px',
    borderLeft: 'none',
  },
  icon: {
    height: '16px',
    width: '16px',
    marginTop: '1.75px',
    marginRight: '8px',
    flex: '0 0 auto',
    alignSelf: 'flex-start',
  },
  firstRow: {
    display: 'flex',
    alignItems: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  showMoreContainer: {
    display: 'inline-flex',
    color: BLUE_LIGHT,
    margin: '0px 4px',
    flex: '0 0 90px', // Space enough for the 'Show/Hide details' text and the caret icon
    justifyContent: 'flex-end',
  },
  showMoreIcon: {
    alignSelf: 'center',
    ':hover': {
      cursor: 'pointer',
    },
  },
  caretIcon: {paddingLeft: '4px'},
  rotatedIcon: {
    transform: 'rotate(180deg)',
    paddingRight: '4px',
  },
  customActionButton: {
    margin: '0px 4px',
    flex: '0 0 auto',
    verticalAlign: 'top',
    display: 'inline-block',
  },
  rightAlignAccordionButton: {
    marginLeft: 'auto',
    paddingLeft: '8px',
    flex: '0 0 auto',
    alignSelf: 'flex-start',
    display: 'inline-flex',
    justifyContent: 'flex-end',
  },
  rightAlignActionButton: {
    marginLeft: 'auto',
    paddingLeft: '8px',
    flex: '0 0 auto',
    display: 'inline-flex',
    justifyContent: 'flex-end',
  },
  dismiss: {
    height: '10px',
    width: '10px',
    flex: '0 0 auto',
    // As designed, the dismiss icon should be 12px away from the top edge and right edge of the banner itself.
    // The banner has 8px padding, which is sufficient for all other elements, so add the other 4px here.
    marginTop: '4px',
    marginRight: '4px',
    color: NEAR_BLACK,
    opacity: 0.5,
    ':hover': {
      opacity: 0.8,
      cursor: 'pointer',
    },
  },
  supportingInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  supportingInfoMargin: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: '24px',
  },
});

/** @package */
export const DEFAULT_LESS_LABEL = 'Hide details';
/** @package */
export const DEFAULT_MORE_LABEL = 'Show details';

/** @package */
export function AccordionButton({
  showMore,
  setShowMore,
  labels,
  onClickAccordionButton,
}: {
  showMore: boolean;
  setShowMore: (val: boolean) => void;
  labels?: {moreLabel: string; lessLabel: string};
  onClickAccordionButton?: (newIsExpanded: boolean) => void;
}): JSX.Element {
  return (
    <span className={css(inlineBannerStyles.showMoreContainer)}>
      <Button
        buttonStyle={ButtonStyle.LINK}
        className={css(inlineBannerStyles.showMoreIcon)}
        text={
          showMore ? (labels?.lessLabel ?? DEFAULT_LESS_LABEL) : (labels?.moreLabel ?? DEFAULT_MORE_LABEL)
        }
        onClick={() => {
          if (onClickAccordionButton != null) {
            onClickAccordionButton(!showMore);
          }
          setShowMore(!showMore);
        }}
        dataTestAttrs={{component: 'InlineBanner', element: 'accordionButton'}}
        aria-expanded={showMore}
      />
      <Icon
        icon="caret-down"
        className={
          showMore
            ? css(inlineBannerStyles.showMoreIcon, inlineBannerStyles.rotatedIcon)
            : css(inlineBannerStyles.showMoreIcon, inlineBannerStyles.caretIcon)
        }
        onClick={() => {
          setShowMore(!showMore);
        }}
      />
    </span>
  );
}

/** @package */
export function CustomActionButton({
  text,
  onClick,
  href,
  buttonStyle,
}: {
  text: string;
  onClick?: () => void;
  href?: string;
  buttonStyle?: ButtonStyle;
}): JSX.Element {
  const actualButtonStyle = buttonStyle ?? ButtonStyle.LINK;
  return onClick != null ? (
    <Button
      buttonStyle={actualButtonStyle}
      size={actualButtonStyle === ButtonStyle.LINK ? undefined : ButtonSize.SMALL}
      text={text}
      onClick={onClick}
      className={css(inlineBannerStyles.customActionButton)}
      dataTestAttrs={{component: 'InlineBanner', element: 'customActionButton'}}
    />
  ) : (
    <ButtonLink
      buttonStyle={actualButtonStyle}
      size={actualButtonStyle === ButtonStyle.LINK ? undefined : ButtonSize.SMALL}
      text={text}
      href={href}
      // Following two attributes both needed to open link in a new tab while protecting against tabnabbing
      target="_blank"
      rel="noopener noreferrer"
      className={css(inlineBannerStyles.customActionButton)}
      dataTestAttrs={{component: 'InlineBanner', element: 'customActionButton'}}
    />
  );
}

const BACKGROUND_COLOR_BY_BANNER_TYPE: Record<InlineBannerType, StyleDeclaration> = {
  [InlineBannerType.INFO]: inlineBannerStyles.infoColors,
  [InlineBannerType.SUCCESS]: inlineBannerStyles.successColors,
  [InlineBannerType.ATTENTION]: inlineBannerStyles.attentionColors,
  [InlineBannerType.WARNING]: inlineBannerStyles.warningColors,
  [InlineBannerType.ERROR]: inlineBannerStyles.errorColors,
  [InlineBannerType.BETA]: inlineBannerStyles.betaColors,
};

/**
 * Exported for testing
 */
export const ROLE_BY_BANNER_TYPE: Record<InlineBannerType, HTMLAttributes<HTMLDivElement>['role']> = {
  [InlineBannerType.INFO]: 'region',
  [InlineBannerType.SUCCESS]: 'status',
  [InlineBannerType.ATTENTION]: 'status',
  [InlineBannerType.WARNING]: 'alert',
  [InlineBannerType.ERROR]: 'alert',
  [InlineBannerType.BETA]: 'region',
};

/**
 * Each banner type has a default icon and a list of allowed override icons.
 * New override icons should be approved by design in #design-systems.
 */
const ICON_BY_BANNER_TYPE = {
  [InlineBannerType.INFO]: {default: 'info-circle', allowedOverrides: ['sparkles']},
  [InlineBannerType.SUCCESS]: {default: 'check', allowedOverrides: []},
  [InlineBannerType.ATTENTION]: {default: 'info-circle', allowedOverrides: ['lock']},
  [InlineBannerType.WARNING]: {default: 'exclamation-triangle', allowedOverrides: ['box-archive', 'gauge']},
  [InlineBannerType.ERROR]: {default: 'exclamation-circle', allowedOverrides: ['gauge-max']},
  // Beta banners should not have an icon, one is defined here for TS reasons
  [InlineBannerType.BETA]: {default: 'notdef', allowedOverrides: []},
} satisfies Record<InlineBannerType, {default: IconName; allowedOverrides: Array<IconName>}>;

const ICON_COLOR_BY_BANNER_TYPE: Record<InlineBannerType, StyleDeclaration> = {
  [InlineBannerType.INFO]: inlineBannerStyles.infoIcon,
  [InlineBannerType.SUCCESS]: inlineBannerStyles.successIcon,
  [InlineBannerType.ATTENTION]: inlineBannerStyles.attentionIcon,
  [InlineBannerType.WARNING]: inlineBannerStyles.warningIcon,
  [InlineBannerType.ERROR]: inlineBannerStyles.errorIcon,
  [InlineBannerType.BETA]: inlineBannerStyles.betaIcon,
};

export interface InlineBannerProps<T extends InlineBannerType = InlineBannerType> {
  /** What sort of feedback the banner is intended to convey */
  bannerType: T;
  /**
   * Which icon to use. If unspecified (which should be the common case), the default defined in
   * `ICON_BY_BANNER_TYPE` will be used.
   *
   * Otherwise, you can use one of the allowed overrides defined in
   * `ICON_BY_BANNER_TYPE[bannerType].allowedOverrides`.
   */
  icon?:
    | (typeof ICON_BY_BANNER_TYPE)[T]['default']
    | (typeof ICON_BY_BANNER_TYPE)[T]['allowedOverrides'][number];
  /** Whether to hide the icon on the banner. Only supported for INFO and BETA banners. */
  hideIcon?: T extends InlineBannerType.INFO ? boolean : T extends InlineBannerType.BETA ? true : false;
  /** Whether to hide the background on the banner. */
  hideBackground?: boolean;
  /** A short piece of bold text to be displayed in the banner header, if not included header will not render. Either `text` or `supportingInfo` must be non-null. */
  text?: ReactNode;
  /** Additional subtext or React elements (e.g. images) to be displayed below the header, or by itself if `text` is not defined. Either `text` or `supportingInfo` must be non-null. */
  supportingInfo?: ReactNode;
  /** Whether to display an action button at the end of the header text. */
  action?: InlineBannerActionProps;
  /** What should happen when the X button at the end of the banner is clicked. */
  onDismiss?: () => void;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * A banner to provide feedback and/or additional context for surrounding content.
 * @param {InlineBannerProps} props Component props
 * @param {InlineBannerType} props.bannerType What sort of feedback the banner is intended to convey
 * @param {boolean} props.hideIcon Whether to hide the icon on the banner. Only supported for INFO banners.
 * @param {ReactNode} props.text A short piece of text to be displayed in the banner header.
 * @param {ReactNode} props.supportingInfo Additional text or React elements (e.g. images) to be displayed in the bottom half of the banner.
 * @param {Object} props.action Whether to display an action button at the end of the header text.
 * @param {InlineBannerActionType} props.action.actionType What sort of action button should be displayed.
 * @param {string} props.action.customActionText For a custom action button, what the button should read.
 * @param {function} props.action.customActionOnClick For a custom action button, what should happen when the button is clicked.
 * @param {boolean} props.action.rightAlign Whether the action button should be aligned with the right-hand side of the banner instead of the end of the banner text
 * @param {function} props.onDismiss What should happen when the X button at the end of the banner is clicked.
 * @constructor
 */
export default function InlineBanner<T extends InlineBannerType = InlineBannerType>({
  action,
  bannerType,
  icon,
  hideIcon,
  onDismiss,
  supportingInfo,
  text,
  dataTestAttrs,
  hideBackground,
}: InlineBannerProps<T>): JSX.Element {
  assertAndContinue(
    text != null || supportingInfo != null,
    'Do not render InlineBanner without text or supportingInfo'
  );
  assertAndContinue(hideIcon || bannerType !== InlineBannerType.BETA, 'Beta banners should hide icon');

  const labelId = useId();
  const descriptionId = useId();

  const shouldShowActionButton = action != null && action.actionType !== InlineBannerActionType.ACCORDION;
  let rightAlignAction: boolean = false;
  if (shouldShowActionButton) {
    if (isCustomActionProps(action)) {
      rightAlignAction = action.rightAlign ?? false;
    } else {
      rightAlignAction = action.rightAlignActionButton ?? false;
    }
  }
  const shouldShowAccordionButton = action != null && action.actionType !== InlineBannerActionType.CUSTOM;
  let rightAlignAccordion: boolean = false;
  if (shouldShowAccordionButton) {
    if (isAccordionActionProps(action)) {
      rightAlignAccordion = action.rightAlign ?? false;
    } else {
      rightAlignAccordion = action.rightAlignAccordionButton ?? false;
    }
  }

  const [showMore, setShowMore] = useState(
    action?.actionType !== InlineBannerActionType.CUSTOM ? (action?.initialShowMore ?? false) : false
  );

  function renderActionButton(): JSX.Element | null {
    if (shouldShowActionButton) {
      return (
        <CustomActionButton
          text={action.actionText}
          onClick={action.actionOnClick}
          href={action.actionHref}
          buttonStyle={action.buttonStyle}
        />
      );
    }
    return null;
  }

  function renderAccordionButton(): JSX.Element | null {
    if (shouldShowAccordionButton && supportingInfo != null) {
      return (
        <AccordionButton
          showMore={showMore}
          setShowMore={setShowMore}
          labels={action.accordionLabels}
          onClickAccordionButton={
            action.actionType === InlineBannerActionType.ACCORDION ? action.onClickAccordionButton : undefined
          }
        />
      );
    }
    return null;
  }

  function renderBannerIcon(): JSX.Element {
    return (
      <>
        {!hideIcon && (
          <Icon
            icon={icon ?? ICON_BY_BANNER_TYPE[bannerType].default}
            className={css(inlineBannerStyles.icon, ICON_COLOR_BY_BANNER_TYPE[bannerType])}
          />
        )}
      </>
    );
  }

  return (
    <div
      className={classNames(
        css(
          inlineBannerStyles.container,
          hideBackground
            ? inlineBannerStyles.containerNoBackground
            : BACKGROUND_COLOR_BY_BANNER_TYPE[bannerType]
        )
      )}
      role={ROLE_BY_BANNER_TYPE[bannerType]}
      {...(text != null ? {'aria-labelledby': labelId} : {})}
      {...(supportingInfo != null && (!shouldShowAccordionButton || showMore)
        ? {'aria-describedby': descriptionId}
        : {})}
      {...getTestProps(dataTestAttrs ?? {component: 'InlineBanner', element: 'root'})}
    >
      {text != null && (
        <div className={css(inlineBannerStyles.firstRow)}>
          {renderBannerIcon()}
          {bannerType === InlineBannerType.BETA && (
            <span className={css(inlineBannerStyles.betaBadge)}>
              <BetaBadge />
            </span>
          )}
          <span>
            <span id={labelId}>{text}</span>
            {!rightAlignAction && renderActionButton()}
            {!rightAlignAccordion && renderAccordionButton()}
          </span>
          {rightAlignAction && (
            <div
              className={css(inlineBannerStyles.rightAlignActionButton)}
              {...getTestProps({
                component: 'InlineBanner',
                element: 'rightAlignButtonWrapper',
                key: 'action',
              })}
              data-amp-safe={false}
            >
              {renderActionButton()}
            </div>
          )}
          {(rightAlignAccordion || onDismiss != null) && (
            <span
              className={css(inlineBannerStyles.rightAlignAccordionButton)}
              {...getTestProps({
                component: 'InlineBanner',
                element: 'rightAlignButtonWrapper',
                key: 'accordion',
              })}
              data-amp-safe={false}
            >
              {rightAlignAccordion && renderAccordionButton()}
              {onDismiss != null && (
                <Icon
                  icon="xmark-large"
                  className={css(inlineBannerStyles.dismiss)}
                  onClick={() => {
                    onDismiss();
                  }}
                />
              )}
            </span>
          )}
        </div>
      )}
      {supportingInfo != null && (!shouldShowAccordionButton || showMore) && (
        <div
          className={classNames({
            [css(inlineBannerStyles.supportingInfo)]: text == null && !hideIcon,
            [css(inlineBannerStyles.supportingInfoMargin)]: text != null && !hideIcon,
          })}
          id={descriptionId}
        >
          {/* Render banner icon with supporting text if no title or heading text is provided */}
          {text == null && renderBannerIcon()}
          {supportingInfo}
        </div>
      )}
    </div>
  );
}
